import * as THREE from "three";
import { groundAt } from "../../terrain";
import { lib } from "../../views/common";
import { ROAD_KITS } from "../kits";
import { SkinKits } from "./runtime";
import { KitMaterials } from "./kitMaterial";
import { KitClock } from "./clock";
import { KIT_BUDGET, type RoadKit, type RoadPiece } from "./types";

/**
 * A road cell for the dressing: where it is, which way the road runs through it (unit x, z on the map), at a bend the
 * direction of its outer corner (`turn`: in-heading − out-heading, unnormalised), and whether it takes no dressing at
 * all (`bare`: a plank bridge, a special tile with its own model).
 */
export interface RoadSpot {
  readonly x: number; readonly z: number; readonly dx: number; readonly dz: number;
  readonly turn?: readonly [number, number] | null;
  readonly bare?: boolean;
}

/** One prop's place: its piece index, position, yaw, height above the ground and how far down the road it is (cells from the portal). */
export interface RoadPlacement { piece: number; x: number; z: number; yaw: number; lift: number; along: number }

/** Where each prop of a road kit goes along a road (pure: tested). Verge props alternate sides; at most the budget. */
export function roadPlacements(kit: Pick<RoadKit, "pieces">, spots: readonly RoadSpot[]): RoadPlacement[] {
  const out: RoadPlacement[] = [];
  kit.pieces.forEach((p, k) => {
    const every = Math.max(1, Math.round(p.every));
    const on = p.on ?? "any", lift = p.lift ?? 0;
    let side = 1;
    for (let i = Math.max(0, Math.round(p.phase ?? k)) % every; i < spots.length; i += every) {
      const s = spots[i];
      if (s.bare) continue;
      const turn = !!s.turn;
      if ((on === "straight" && turn) || (on === "turn" && !turn)) continue;
      // a turn piece is authored as the outer corner toward glTF +X +Z: turn it to the bend's outside
      const yaw = on === "turn" && s.turn ? Math.atan2(s.turn[0], s.turn[1]) - Math.PI / 4 : Math.atan2(s.dx, s.dz);
      if (p.place === "centre") out.push({ piece: k, x: s.x, z: s.z, yaw, lift, along: i });
      else {
        const d = p.side ?? 0.42;
        // the verge: off the centre line, square to the road, facing it
        out.push({ piece: k, x: s.x + s.dz * d * side, z: s.z - s.dx * d * side, yaw: yaw + (side > 0 ? -Math.PI / 2 : Math.PI / 2), lift, along: i });
        side = -side;
      }
      if (out.length >= KIT_BUDGET.RoadInstances) return;
    }
  });
  return out.slice(0, KIT_BUDGET.RoadInstances);
}

/** The road's cells as dressing spots (pure: tested): headings, bends, and the cells left bare. The base cell is dropped. */
export function roadSpots(cells: readonly { x: number; y: number }[], bare: (i: number) => boolean = () => false): RoadSpot[] {
  return cells.map((c, i) => {
    const a = cells[Math.max(0, i - 1)], b = cells[Math.min(cells.length - 1, i + 1)];
    const dx = b.x - a.x, dz = b.y - a.y, l = Math.hypot(dx, dz) || 1;
    let turn: [number, number] | null = null;
    if (i > 0 && i < cells.length - 1) {
      const ix = c.x - a.x, iz = c.y - a.y, ox = b.x - c.x, oz = b.y - c.y;
      if (ix !== ox || iz !== oz) turn = [ix - ox, iz - oz];
    }
    return { x: c.x, z: c.y, dx: dx / l, dz: dz / l, turn, bare: bare(i) };
  }).slice(0, -1);
}

/**
 * One frame of the enemies' light on the reacting props (pure: tested): each rises at once to what the enemies near it
 * want and otherwise falls by `drop`, never under 0. Returns whether any changed (the attribute then re-uploads).
 */
export function fadeLit(lit: Float32Array, want: Float32Array, drop: number): boolean {
  let changed = false;
  for (let i = 0; i < lit.length; i++) {
    const next = Math.max(want[i], lit[i] - drop, 0);
    if (next !== lit[i]) { lit[i] = next; changed = true; }
  }
  return changed;
}

/** How far either side (cells) the ground's slope under a road piece is measured: about half a piece. */
const SLOPE_STEP = 0.3;

/** The drawn ground's normal at (x, z), into `out` (pure: tested). */
export function groundNormal(ground: (x: number, z: number) => number, x: number, z: number, out: THREE.Vector3): THREE.Vector3 {
  const hx = (ground(x + SLOPE_STEP, z) - ground(x - SLOPE_STEP, z)) / (2 * SLOPE_STEP);
  const hz = (ground(x, z + SLOPE_STEP) - ground(x, z - SLOPE_STEP)) / (2 * SLOPE_STEP);
  return out.set(-hx, 1, -hz).normalize();
}

/** What answers the enemies on a built road (RoadPiece.react): the reacting instances, their glow, and the cells near each. */
interface Reactor {
  readonly attr: THREE.InstancedBufferAttribute;
  readonly lit: Float32Array;
  readonly want: Float32Array;
  readonly px: Float32Array; readonly pz: Float32Array;
  readonly glow: number; readonly r2: number; readonly fall: number;
  /** Cell key (x * 4096 + z) → the instances within reach of that cell. */
  readonly near: Map<number, number[]>;
}

/** Enemies as the road view holds them (only `alive` and the position are read). */
export type RoadWalkers = readonly { readonly alive: boolean; readonly x: { toNumber(): number }; readonly y: { toNumber(): number } }[];

/**
 * A road skin's dressing (kit/types.ts RoadKit): its props strewn along the built road, one InstancedMesh per prop
 * kind (a road kit of three props is three draws however long the road), animated by the kit material's instanced
 * variant: each instance carries how far down the road it stands (`kitAlong`: a `travel` wave runs from the portal to
 * the fortress, the way the enemies walk) and how lit it is by the enemies passing (`kitLit`, RoadPiece.react).
 * Rebuilt with the road (views/road.ts).
 */
export class RoadDecor {
  private readonly reactors: Reactor[] = [];
  private constructor() {}

  /**
   * Replaces the dressing in `group` for the road `cells` (portal … base) under the worn road skin `skin`; `bare(i)`
   * says a cell takes no dressing (a bridge, a special tile). Returns what reacts to the enemies, or null.
   */
  /**
   * `ground` is the height of the ground as drawn (GroundMesh.drawnAt in a level; the hills alone, groundAt, where
   * there is no ground mesh). The props stand on it, and the pieces laid on the road (`place: "centre"`: kerbs, slabs)
   * lie in its slope, so on a ramp a kerb follows the paving instead of standing level with one end buried and the
   * other in the air (in-game check 2026-10-22); verge props stay upright.
   */
  static build(group: THREE.Group, skin: string | null, cells: readonly { x: number; y: number }[], bare?: (i: number) => boolean, ground: (x: number, z: number) => number = groundAt): RoadDecor | null {
    for (const c of [...group.children]) if (c.userData.tdRoadDecor === true) { group.remove(c); (c as THREE.Mesh).geometry.dispose(); }
    const kit = skin ? ROAD_KITS[skin] : undefined;
    const L = lib();
    if (!kit?.asset || !L || cells.length < 2) return null;
    const pieces = SkinKits.pieces(L, kit.asset);
    if (!pieces) return null;
    const placed = roadPlacements(kit, roadSpots(cells, bare));
    const rows = kit.pieces.map((p) => ({ motion: p.motion ?? pieces.get(p.piece)?.motion, detail: p.detail }));
    const decor = new RoadDecor();
    kit.pieces.forEach((p, k) => {
      const piece = pieces.get(p.piece);
      const mine = placed.filter((x) => x.piece === k);
      if (!piece || mine.length === 0) return;
      const geo = piece.geometry.clone();
      const w = piece.geometry.getAttribute("kitWeight");
      const data = new Float32Array(w.count * 2);
      for (let i = 0; i < w.count; i++) { data[i * 2] = k; data[i * 2 + 1] = w.getX(i); }
      geo.setAttribute("kitData", new THREE.BufferAttribute(data, 2));
      geo.setAttribute("kitAlong", new THREE.InstancedBufferAttribute(Float32Array.from(mine, (x) => x.along), 1));
      const lit = new THREE.InstancedBufferAttribute(new Float32Array(mine.length), 1);
      geo.setAttribute("kitLit", lit);
      const mesh = new THREE.InstancedMesh(geo, KitMaterials.variant(piece.material, `${kit.id}-road`, rows, true), mine.length);
      const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3().setScalar(p.scale ?? 1), up = new THREE.Vector3(0, 1, 0);
      const tilt = new THREE.Quaternion(), n = new THREE.Vector3();
      mine.forEach((x, i) => {
        q.setFromAxisAngle(up, x.yaw);
        if (p.place === "centre") q.premultiply(tilt.setFromUnitVectors(up, groundNormal(ground, x.x, x.z, n)));
        m.compose(new THREE.Vector3(x.x, ground(x.x, x.z) + x.lift, x.z), q, s);
        mesh.setMatrixAt(i, m);
      });
      mesh.userData.tdRoadDecor = true;
      mesh.userData.tdLift = p.lift ?? 0;
      mesh.userData.ownsGeometry = true;
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.onBeforeRender = () => KitClock.tick();
      mesh.computeBoundingSphere();
      group.add(mesh);
      if (p.react) decor.reactors.push(RoadDecor.reactor(p, lit, mine));
    });
    return decor.reactors.length ? decor : null;
  }

  private static reactor(p: RoadPiece, attr: THREE.InstancedBufferAttribute, mine: readonly RoadPlacement[]): Reactor {
    const r = p.react!.radius ?? 0.9, reach = Math.ceil(r);
    const near = new Map<number, number[]>();
    mine.forEach((x, i) => {
      const cx = Math.round(x.x), cz = Math.round(x.z);
      for (let a = -reach; a <= reach; a++) for (let b = -reach; b <= reach; b++) {
        const key = (cx + a) * 4096 + (cz + b);
        const list = near.get(key);
        if (list) list.push(i); else near.set(key, [i]);
      }
    });
    attr.setUsage(THREE.DynamicDrawUsage);
    return {
      attr, lit: attr.array as Float32Array, want: new Float32Array(mine.length),
      px: Float32Array.from(mine, (x) => x.x), pz: Float32Array.from(mine, (x) => x.z),
      glow: p.react!.glow, r2: r * r, fall: p.react!.glow / Math.max(0.05, p.react!.fade ?? 0.8), near,
    };
  }

  /** Per frame: the reacting props light up near the enemies and fade behind them. No allocation. */
  update(dt: number, walkers: RoadWalkers | null): void {
    for (const R of this.reactors) {
      R.want.fill(0);
      if (walkers) {
        for (const e of walkers) {
          if (!e.alive) continue;
          const ex = e.x.toNumber(), ez = e.y.toNumber();
          const list = R.near.get(Math.round(ex) * 4096 + Math.round(ez));
          if (!list) continue;
          for (const i of list) {
            const dx = R.px[i] - ex, dz = R.pz[i] - ez, d2 = dx * dx + dz * dz;
            if (d2 < R.r2) R.want[i] = Math.max(R.want[i], R.glow * (1 - d2 / R.r2));
          }
        }
      }
      if (fadeLit(R.lit, R.want, R.fall * dt)) R.attr.needsUpdate = true;
    }
  }
}
